import MainLayout from "@/components/layout/admin/MainLayout.vue";
import Select2Multiple from "v-select2-multiple-component";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import axios from "axios";
import { required } from "vuelidate/lib/validators";
import ValidationService from "@/services/ValidationService";

export default {
  name: "subjectImport",
  components: {
    MainLayout,
    Select2Multiple,
    Loading,
  },
  mixins: [ValidationService],

  data() {
    return {
      isLoading: false,
      acdYearList: [],
      acdYearId: null,
      templateAcdYearId: null,
      file_name: "",
      invalidFile: false,
      invalidFileSize: false,
      submitted: false,
      disableImportBtn: false,
      isCommitting: false,
      rowsNormalized: [],
      toCreate: [],
      previewErrors: [],
    };
  },

  validations: {
    acdYearId: { required },
    file_name: { required },
  },

  computed: {
    disabledTemplateBtn() {
      return !this.templateAcdYearId;
    },

    canConfirmSave() {
      return this.previewErrors.length === 0 && this.toCreate.length > 0;
    },

    previewRows() {
      return this.rowsNormalized;
    },

    disableCommitBtn() {
      return this.isCommitting || !this.canConfirmSave;
    },

    previewVisible() {
      return this.previewErrors.length > 0 || this.rowsNormalized.length > 0;
    },

    canShowPreviewList() {
      return this.previewErrors.length === 0 && this.previewRows.length > 0;
    },

    selectedAcademicYearName() {
      const selected = this.acdYearList.find(
        (period) => String(period.id) === String(this.acdYearId)
      );

      return selected?.text || this.previewRows[0]?.academic_year_name || "";
    },
  },

  methods: {
    async getParentAcdYearList() {
      try {
        this.isLoading = true;
        const response = await axios.get("period/periodList");
        if (response.data) {
          this.acdYearList = response.data.data || [];
        }
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isLoading = false;
      }
    },

    chooseFile() {
      this.$refs.fileUploader.value = null;
      this.file_name = "";
      this.invalidFile = false;
      this.invalidFileSize = false;
      this.clearPreview();
      this.$refs.fileUploader.click();
      this.submitted = false;
    },

    onFileChange(event) {
      const file = event.target.files[0];
      this.invalidFile = false;
      this.invalidFileSize = false;

      if (!file) return;

      const allowedTypes = [".xls", ".xlsx"];
      const ext = `.${file.name.split(".").pop().toLowerCase()}`;

      if (!allowedTypes.includes(ext)) {
        this.invalidFile = true;
        this.file_name = "";
        this.$notification.error("File type not supported!", {
          timer: 3,
          position: "bottomCenter",
        });
        return;
      }

      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        this.invalidFileSize = true;
        this.file_name = file.name;
        return;
      }

      this.file_name = file.name;
      this.clearPreview();
    },

    async templateDownload() {
      if (this.disabledTemplateBtn) return;

      this.isLoading = true;
      try {
        const response = await axios.get("/subject/template-download", {
          params: { academic_year_id: this.templateAcdYearId },
          responseType: "blob",
        });
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const selectedYear = this.acdYearList.find(y => y.id == this.templateAcdYearId);
        const now = new Date();
        const dateStr = `${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, '0')}_${String(now.getDate()).padStart(2, '0')}`;
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `${selectedYear.text}_subjects_${dateStr}.xlsx`;
        document.body.appendChild(link);
        document.body.removeChild(link);
        link.click();
      } catch (error) {
        this.handleImportError(error, "Failed to download subject template.");
      } finally {
        this.isLoading = false;
      }
    },

    async confirmImport() {
      this.submitted = true;
      this.clearPreview();

      if (this.$v.$invalid) return;

      const file = this.$refs.fileUploader.files[0];
      if (!file) {
        this.invalidFile = true;
        this.file_name = "";
        return;
      }

      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        this.invalidFileSize = true;
        return;
      }

      await this.previewImport(file);
    },

    async previewImport(file) {
      this.isLoading = true;
      this.disableImportBtn = true;

      const formData = new FormData();
      formData.append("academic_year_id", this.acdYearId);
      formData.append("file", file);

      try {
        const response = await axios.post("/subject/import/preview", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        this.applyPreviewResponse(response.data);
        if ((response.data.errors || []).length === 0) {
          this.$notification.success("Subject import preview is ready.", {
            timer: 3,
            position: "bottomCenter",
          });
        }
      } catch (error) {
        this.handleImportError(error, "Subject import preview failed.");
      } finally {
        this.disableImportBtn = false;
        this.isLoading = false;
      }
    },

    async confirmSave() {
      if (this.disableCommitBtn) return;

      this.isLoading = true;
      this.isCommitting = true;

      try {
        const response = await axios.post("/subject/import/commit", {
          academic_year_id: this.acdYearId,
          to_create: this.toCreate,
        });
        this.$notification.success(response.data.message || "Subject import completed successfully.", {
          timer: 3,
          position: "bottomCenter",
        });
        this.showList();
      } catch (error) {
        this.handleImportError(error, "Subject import save failed.");
      } finally {
        this.isCommitting = false;
        this.isLoading = false;
      }
    },

    resetAll() {
      this.submitted = false;
      this.acdYearId = null;
      this.templateAcdYearId = null;
      this.file_name = "";
      this.invalidFile = false;
      this.invalidFileSize = false;
      this.clearPreview();
      this.clearValidationErrors();

      if (this.$refs.fileUploader) {
        this.$refs.fileUploader.value = null;
      }

      this.getParentAcdYearList();
    },

    applyPreviewResponse(data) {
      this.toCreate = data.to_create || [];
      this.rowsNormalized = data.rows_normalized || this.toCreate;
      this.previewErrors = data.errors || [];
    },

    clearPreview() {
      this.rowsNormalized = [];
      this.toCreate = [];
      this.previewErrors = [];
    },

    handleAcademicYearChange(newValue, oldValue) {
      if (oldValue !== null && newValue !== oldValue && this.previewVisible) {
        this.clearPreview();
      }
    },

    handleImportError(error, fallbackMessage) {
      const data = error.response?.data || {};
      const status = error.response?.status;
      const errors = data.errors || (data.msg ? [data.msg] : []);
      this.previewErrors = Array.isArray(errors) ? errors : Object.values(errors).flat();

      const message = this.previewErrors[0] || data.message || fallbackMessage;

      if (status === 422) {
        const allErrors = this.previewErrors.join('\n');
        alert(allErrors || message);
        this.clearPreview();
      } else {
        this.$notification.error(message, {
          timer: 3,
          position: "bottomCenter",
        });
      }
    },

    showList() {
      this.$router.push({ name: "subjectList" });
    },

    handleError(error) {
      if (error.response && error.response.status === 401) {
        this.$store.commit("logout");
        window.location.replace("login");
      } else {
        console.error(error);
      }
    },
  },

  mounted() {
    this.getParentAcdYearList();
  },

  watch: {
    acdYearId(newValue, oldValue) {
      this.handleAcademicYearChange(newValue, oldValue);
    },
  },
};
